import { useState, useCallback, useEffect } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useDebouncedCallback } from 'use-debounce';
import { Slap, StickerDesign, PaymentMethod } from '../utils/Primitives';

type ParsedRealtimeMessage = {
  type: string;
  slap?: Slap;
  stickerDesign?: StickerDesign;
  paymentMethod?: PaymentMethod;
}

interface UseRealtimeOptions {
  url: string;
  onMessage?: (event: MessageEvent) => void;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
}
export const handleRealtimeMessage = (item: any): ParsedRealtimeMessage | null => {
  if (item.symbol) {
    // it is a payment method
    const paymentMethod: PaymentMethod = {
      id: item.id,
      address: item.address,
      name: item.name,
      symbol: item.symbol,
      decimals: item.decimals,
    };
    console.log(`PAYMENT METHOD ${item.id} WITH SYMBOL ${item.symbol}`);
    return {
      type: 'paymentMethod',
      paymentMethod,
    } as ParsedRealtimeMessage;
  }
  else if (item.slapId) {
    // it is a slap
    const slapId = item.slapId;
    console.log(`Handling slap: ${slapId}`);
    const stickerDesignId = item.stickerDesignId;
    const placeId = item.placeId;
    const size = item.size;
    const player = item.playerAddress;
    const slapTimestamp = item.slappedAt;
    const slapDateTime = new Date(slapTimestamp);
    console.log(`SLAP ${slapId} SLAPPED AT ${slapDateTime}`);
    const objectiveIds = item.objectiveIds;
    console.log(`SLAP ${slapId} HAS OBJECTIVES ${objectiveIds}`);
    const slap: Slap = {
      id: slapId,
      sticker: stickerDesignId,
      place: placeId,
      size: size,
      time: slapTimestamp,
      player: player,
      objectiveIds: objectiveIds,
    };
    return {
      type: 'slap',
      slap,
    } as ParsedRealtimeMessage;
  }
  else if (item.metadataLoaded) {
    // it is a sticker design
    const stickerDesignId = item.stickerId;
    const stickerPublishTimestamp = item.publishedAt;
    const stickerDateTime = new Date(stickerPublishTimestamp);
    const stickerDesign: StickerDesign = {
      id: stickerDesignId,
      name: item.name,
      originalPublisher: item.originalPublisher,
      currentPublisher: item.currentPublisher,
      payoutAddress: item.payoutAddress,
      publishedAt: stickerPublishTimestamp,
      paymentMethodId: item.paymentMethodId,
      price: item.price,
      limit: item.limit,
      endTime: item.endTime,
      aspectRatio: item.aspectRatio,
      imageCID: item.imageCID,
      metaCID: item.metadataCID,
      filename: item.filename,
    };
    console.log(`STICKER ${stickerDesignId} CREATED AT ${stickerDateTime}`);
    return {
      type: 'sticker',
      stickerDesign,
    } as ParsedRealtimeMessage;
  }
  else {
    console.error(`UNKNOWN ITEM TYPE IN REALTIME MESSAGE: ${item}`);
    return null;
  }
}

export const useRealtime = ({
  url,
  onMessage,
  onOpen,
  onClose,
  onError,
}: UseRealtimeOptions) => {
    const [socketUrl, setSocketUrl] = useState(url);
    const [slapHistory, setSlapHistory] = useState<Slap[]>([]);
    const [debouncedSlapHistory, setDebouncedSlapHistory] = useState<Slap[]>([]);
    const [latestSlap, setLatestSlap] = useState<Slap | null>(null);
    const [latestStickerDesign, setLatestStickerDesign] = useState<StickerDesign | null>(null);
    const [latestPaymentMethod, setLatestPaymentMethod] = useState<PaymentMethod | null>(null);

    const debouncedSetSlapHistory = useDebouncedCallback(
      (slaps: Slap[]) => {
        setDebouncedSlapHistory(slaps);
      },
      1000 // 1 second debounce
    );

    const { lastJsonMessage, readyState } = useWebSocket(socketUrl);

    useEffect(() => {
      if (!lastJsonMessage || typeof lastJsonMessage !== 'object') {
        return;
      }
      const records = Array.isArray((lastJsonMessage as any).Records)
        ? (lastJsonMessage as any).Records
        : null;
      if (!records) {
        return;
      }
      console.log('message received:', lastJsonMessage);
      for (const record of records) {
          console.log(record);
          console.log(`Record body: ${record.body}`);
          let parsedRecordBody = null;
          try {
            parsedRecordBody = JSON.parse(record.body);
          }
          catch (e) {
            console.error(`ERROR PARSING RECORD BODY: ${e}`);
          }
          if (!parsedRecordBody) {
            console.error(`Empty parsedRecordBody`);
            continue;
          }
          const parsedMessage = handleRealtimeMessage(parsedRecordBody);
          if (!parsedMessage) {
            continue;
          }
          if (parsedMessage.type === 'slap' && parsedMessage.slap) {
            setLatestSlap(parsedMessage.slap);
            setSlapHistory(prev => {
              const newSlapHistory = [...prev, parsedMessage.slap as Slap];
              debouncedSetSlapHistory(newSlapHistory);
              return newSlapHistory;
            });
          } else if (parsedMessage.type === 'sticker' && parsedMessage.stickerDesign) {
            setLatestStickerDesign(parsedMessage.stickerDesign);
          } else if (parsedMessage.type === 'paymentMethod' && parsedMessage.paymentMethod) {
            setLatestPaymentMethod(parsedMessage.paymentMethod);
          }
      }
    }, [lastJsonMessage, debouncedSetSlapHistory]);

    const connectionStatus = {
      [ReadyState.CONNECTING]: 'Connecting',
      [ReadyState.OPEN]: 'Open',
      [ReadyState.CLOSING]: 'Closing',
      [ReadyState.CLOSED]: 'Closed',
      [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

  return {
    lastJsonMessage,
    latestSlap,
    slapHistory: debouncedSlapHistory,
    latestStickerDesign,
    latestPaymentMethod,
    connectionStatus,
   };
};
